import { useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRefinementList } from 'react-instantsearch';
import { useTranslation } from 'next-i18next';
import Skeleton from 'react-loading-skeleton';
import cn from 'classnames';

import { useUI } from '@context/GlobalUIContext';
import { useStoresUI } from '@context/StoresContext';

import Toggle from '_components/UI/Toggle';

import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import styles from './PlpSortBar.module.scss';

const FavouriteStoreButton = dynamic(() => import('./FavouriteStoreButton'), {
  loading: () => <Skeleton width={100} />,
  ssr: false,
});

const StoreAvailabilityToggle = ({ locale = 'en' }: { locale?: string }) => {
  const { t } = useTranslation(['searchResults', 'common']);
  const { favouriteStoreDetails } = useStoresUI();
  const { openStoresModal, displayStoresModal } = useUI();

  const { items, refine } = useRefinementList({
    attribute: AlgoliaFacets.StoreInventory,
    limit: 100,
  });

  const activeStoreFacet = items.find((item) => item.isRefined);
  const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;
  const favouriteStoreName =
    favouriteStoreDetails?.data?.store_name ??
    t('searchResults:search.facets.storeAvailability.myStore');
  // Number of products in the current result set carried by the favourite store.
  // Undefined when no store is set or the store falls outside the facet values
  // Algolia returned, in which case the suffix is simply omitted.
  const favouriteStoreCount = favouriteStoreId
    ? items.find((item) => item.label === favouriteStoreId)?.count
    : undefined;
  // The visible label only shows the "Available at"/"At" prefix — the store
  // name and count are separate siblings (`FavouriteStoreButton`, the count
  // span), so the accessible name needs to spell out the full sentence.
  const toggleAriaLabel = `${t(
    'searchResults:search.facets.storeAvailability.availableAt'
  )} ${favouriteStoreName}${
    favouriteStoreCount === undefined
      ? ''
      : ` ${t('searchResults:search.facets.storeAvailability.count', {
          value: favouriteStoreCount.toLocaleString(locale),
        })}`
  }`;
  const applyStoreFacet = useCallback(
    (storeId: string) => {
      refine(storeId);
    },
    [refine]
  );
  const isStoreFacetActive =
    !!favouriteStoreId && activeStoreFacet?.label === favouriteStoreId;

  const modalFlag = useRef('initial');
  const handleToggle = () => {
    if (!favouriteStoreId) {
      openStoresModal();
      return;
    }

    modalFlag.current = 'handleToggle';
    applyStoreFacet(favouriteStoreId);
  };

  // Handles case where a store is applied in the modal, we want the facet to be applied
  useEffect(() => {
    // Skip the effect on initial render and when the wrapper is clicked
    if (
      modalFlag.current === 'initial' ||
      modalFlag.current === 'handleToggle'
    ) {
      modalFlag.current = favouriteStoreId;
      return;
    }

    // Skip when the store is being hydrated from cookie (previous value was undefined)
    if (modalFlag.current === undefined) {
      modalFlag.current = favouriteStoreId;
      return;
    }

    // Only run the effect when the modal closes
    if (displayStoresModal) {
      return;
    }

    // If a new store is selected, remove the old one and apply the new one
    else if (activeStoreFacet?.label !== favouriteStoreId) {
      if (activeStoreFacet?.label) {
        applyStoreFacet(activeStoreFacet?.label);
      }
      if (favouriteStoreId) {
        applyStoreFacet(favouriteStoreId);
      }
    }
  }, [
    displayStoresModal,
    favouriteStoreId,
    applyStoreFacet,
    activeStoreFacet?.label,
  ]);

  return (
    <div className={styles.storeToggle}>
      <Toggle
        checked={isStoreFacetActive}
        onChange={handleToggle}
        aria-label={toggleAriaLabel}
        // Fixed-size beside the store name, which is what shrinks when the row
        // is too narrow — see `.favouriteStore`.
        className={styles.storeToggleSwitch}
        // The design shortens the prefix on mobile ("At my Store") from the
        // desktop wording ("Available at my Store"). Both are rendered and one is
        // hidden per breakpoint so the copy is right in the server-rendered HTML.
        label={
          <>
            <span className={cn(styles.storeLabel, styles.storeLabelDesktop)}>
              {t('searchResults:search.facets.storeAvailability.availableAt')}
            </span>
            <span className={cn(styles.storeLabel, styles.storeLabelMobile)}>
              {t('searchResults:search.facets.storeAvailability.at')}
            </span>
          </>
        }
      />
      <FavouriteStoreButton />
      {favouriteStoreCount === undefined ? null : (
        <span className={styles.storeCount}>
          {t('searchResults:search.facets.storeAvailability.count', {
            value: favouriteStoreCount.toLocaleString(locale),
          })}
        </span>
      )}
    </div>
  );
};

export default StoreAvailabilityToggle;
